<!-- کامپوننت SohaModal -->
<template>
  <div>
    <!-- activator slot -->
    <div
      v-if="$slots.activator"
      class="soha-modal-activator"
    >
      <slot
        name="activator"
        :open-modal="openModal"
      />
    </div>

    <Dialog
      v-model:visible="isVisible"
      :header="title"
      :modal="true"
      :style="dialogStyle"
      :content-style="contentStyle"
      :closable="!hideClose"
      :dismissable-mask="!noCloseBackDrop"
      :draggable="false"
      :position="position"
      @show="onShow"
      @hide="onHide"
    >
      <template #default>
        <div ref="modalContent" :class="{'p-0': noPadding}">
          <slot name="body" />
        </div>
      </template>

      <template
        #footer
        v-if="!hideFooter"
      >
        <slot name="footer">
          <div />
        </slot>
      </template>
    </Dialog>
  </div>
</template>

<script>
import Dialog from "primevue/dialog";
import { modalService } from "@/core/services/modal-service";
import { mapGetters } from "vuex";

export default {
  name: "SohaModal",

  components: { Dialog },

  props: {
    id: { type: String, required: false, default: null },
    title: { type: String, default: "" },
    size: { type: String, default: "sm" },
    hideFooter: { type: Boolean, default: false },
    hideClose: { type: Boolean, default: false },
    noCloseBackDrop: { type: Boolean, default: false },
    visible: { type: Boolean, default: false },
    scrollable: { type: Boolean, default: true },
    maxHeight: { type: String, default: "90vh" },
    position: { type: String, default: "center" },
    // New prop to remove padding for full-width internal layouts like split-pane
    noPadding: { type: Boolean, default: false },
  },

  emits: ['show', 'update:visible', 'hide'],

  data() {
    return {
      isVisible: this.visible,
      contentHeight: 0,
      windowHeight: 0,
    };
  },

  computed: {
    ...mapGetters(['screenWidth', 'screenHeight']),

    autoScrollable() {
      if (this.scrollable !== null) {
        return this.scrollable;
      }
      return this.contentHeight > this.windowHeight * 0.8;
    },

    dialogStyle() {
      // Support for fullscreen size, overriding max-width restrictions
      if (this.size === 'fullscreen') {
        return { width: '98vw', maxWidth: '100vw', margin: '0 auto' };
      }

      const sizes = {
        sm: '25vw',
        md: '40vw', 
        lg: '50vw',
        xl: '75vw'
      };
      let minWidth = "350px";

      if (this.screenWidth <= 480) {
        minWidth = "300px";
      }

      return { maxWidth: "1100px", minWidth: minWidth, width: sizes[this.size] }
    },
    
    contentStyle() {
      let styleObj = {};

      if (this.scrollable || this.autoScrollable) {
        styleObj = {
          maxHeight: this.maxHeight,
          "overflow-y": "auto",
          padding: "0"
        };
      } else {
        styleObj = {
          overflow: "visible",
        };
      }

      // Force zero padding if requested by the parent component
      if (this.noPadding) {
        styleObj.padding = "0 !important";
        styleObj.overflowX = "hidden";
      }

      return styleObj;
    }
  },

  watch: {
    visible(val) {
      this.isVisible = val;
    },
    isVisible(val) {
      this.$emit("update:visible", val);
    },
  },

  mounted() {
    if (this.id) {
      modalService.register(this.id, {
        show: () => { this.isVisible = true; },
        hide: () => { this.isVisible = false; },
      });
    }
  },

  unmounted() {
    if (this.id) {
      modalService.unregister(this.id);
    }
  },

  methods: {
    updateDimensions() {      
      this.windowHeight = window.innerHeight;      
      if (this.$refs.modalContent) {
        this.$nextTick(() => {
          this.contentHeight = this.$refs.modalContent.scrollHeight;
        })
      }      
    },

    onShow() {
      this.$emit('show');
      this.updateDimensions();
    },

    onHide() {
      this.$emit("hide");
    },
  },
};
</script>